import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App.jsx';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import {
  Clock, CheckCircle2, FileText, Users, ChevronRight,
  Camera, CalendarDays, Cake, TrendingUp, AlertCircle,
  Folder, UserCircle2, Edit3, Save, X, ExternalLink
} from 'lucide-react';

/* ── Helpers ─────────────────────────────────────────────────── */
const BADGE_COLOR = {
  'Ingresado':         { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  'En Revisión':       { bg: '#fefce8', color: '#ca8a04', border: '#fde68a' },
  'En Proceso':        { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
  'Completado':        { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  'Rehusado':          { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  'Reabierto':         { bg: '#fef3c7', color: '#d97706', border: '#fde68a' },
  default:             { bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0' },
};

function EstadoBadge({ estado }) {
  const s = BADGE_COLOR[estado] || BADGE_COLOR.default;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 20,
      fontSize: '0.72rem', fontWeight: 700,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`
    }}>
      {estado || 'Sin estado'}
    </span>
  );
}

function Avatar({ src, name, size = 40, style = {} }) {
  const initials = (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  return src ? (
    <img src={src} alt={name}
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, ...style }} />
  ) : (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg, #003366 0%, #0055a4 100%)',
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.38, ...style
    }}>
      {initials}
    </div>
  );
}

function Spinner() {
  return <div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />;
}

/* ── Tarjeta de Stat ─────────────────────────────────────────── */
function StatCard({ icon: Icon, label, value, color, bg, linkTo }) {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => linkTo && navigate(linkTo)}
      style={{
        background: bg || '#fff', borderRadius: 16, padding: '1.5rem',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0',
        display: 'flex', alignItems: 'center', gap: '1.2rem',
        cursor: linkTo ? 'pointer' : 'default',
        transition: 'all 0.2s', flex: 1, minWidth: 180,
      }}
      onMouseEnter={e => linkTo && (e.currentTarget.style.transform = 'translateY(-3px)')}
      onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
    >
      <div style={{
        width: 52, height: 52, borderRadius: 14, background: color + '22',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={26} color={color} />
      </div>
      <div>
        <div style={{ fontSize: '2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{value ?? '—'}</div>
        <div style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600, marginTop: 4 }}>{label}</div>
      </div>
    </div>
  );
}

/* ── Panel Cumpleaños ────────────────────────────────────────── */
function CumpleanioCard({ persona }) {
  const hoy = new Date();
  const dia = new Date(persona.fecha_nacimiento + 'T12:00:00').getDate();
  const esHoy = dia === hoy.getDate();

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
      padding: '1rem 0.75rem',
      background: esHoy ? 'linear-gradient(135deg, #fffbeb, #fef3c7)' : '#f8fafc',
      border: `2px solid ${esHoy ? '#fbbf24' : '#e2e8f0'}`,
      borderRadius: 16, textAlign: 'center', position: 'relative',
      transition: 'all 0.2s',
    }}>
      {esHoy && (
        <span style={{
          position: 'absolute', top: -10, right: -8, background: '#f59e0b',
          color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20,
        }}>🎉 HOY</span>
      )}
      <Avatar src={persona.avatar_url} name={persona.name} size={52} />
      <div style={{ fontSize: '1.4rem' }}>🎂</div>
      <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e293b', lineHeight: 1.2 }}>
        {persona.name.split(' ')[0]}
      </div>
      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
        {persona.cargo || persona.role}
      </div>
      <div style={{
        background: esHoy ? '#fbbf24' : '#e2e8f0', color: esHoy ? '#fff' : '#64748b',
        borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700,
      }}>
        {dia} de {new Date(0, new Date(persona.fecha_nacimiento + 'T12:00:00').getMonth()).toLocaleString('es-CL', { month: 'long' })}
      </div>
    </div>
  );
}

/* ── Modal editar perfil ────────────────────────────────────── */
function ModalPerfil({ user, onClose, onSaved }) {
  const [cargo, setCargo] = useState(user.cargo || '');
  const [fechaNac, setFechaNac] = useState(() => {
    if (!user.fecha_nacimiento) return '';
    const val = String(user.fecha_nacimiento).trim();
    if (val.includes('/')) {
      const parts = val.split('/');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }
    return val.split('T')[0];
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [preview, setPreview] = useState(user.avatar_url || null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleAvatarChange = e => {
    const f = e.target.files[0];
    if (!f) return;
    setAvatarFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      if (cargo) fd.append('cargo', cargo);
      if (fechaNac) {
        let f = fechaNac;
        if (f.includes('/')) {
          const parts = f.split('/');
          if (parts.length === 3) {
            f = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
          }
        }
        fd.append('fecha_nacimiento', f);
      }
      if (avatarFile) fd.append('avatar', avatarFile);

      const { data } = await api.post('/dashboard/perfil', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Perfil actualizado');
      onSaved(data.user);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.errors?.fecha_nacimiento?.[0] || 
                  err.response?.data?.message || 
                  'Error al guardar el perfil';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Atajo de teclado: Ctrl/Cmd + Enter para guardar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      }
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cargo, fechaNac, avatarFile, saving]);

  return (
    <div 
      className="modal-overlay" 
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1.25rem', zIndex: 1000
      }}
    >
      <div 
        className="modal-content animate-fade-in" 
        style={{ 
          maxWidth: 480, width: '100%', borderRadius: 20, 
          padding: '1.75rem', overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 51, 102, 0.3)',
          background: '#ffffff', border: '1px solid rgba(226, 232, 240, 0.9)'
        }} 
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: '#eff6ff', color: '#003366', padding: 8, borderRadius: 10 }}>
              <User size={20} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#003366', fontWeight: 800 }}>Editar mi Perfil</h3>
              <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Actualice su fotografía, cargo y fecha de nacimiento</span>
            </div>
          </div>
          <button 
            className="modal-close" 
            onClick={onClose}
            disabled={saving}
            style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Avatar */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: '1.5rem' }}>
          <div 
            style={{ position: 'relative', cursor: 'pointer', transition: 'transform 0.15s' }} 
            onClick={() => fileRef.current?.click()}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            <Avatar src={preview} name={user.name} size={92} />
            <div style={{
              position: 'absolute', bottom: 2, right: 2, background: '#003366',
              borderRadius: '50%', padding: 7, color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              border: '2px solid #ffffff'
            }}>
              <Camera size={14} />
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
          </div>
          <span style={{ fontSize: '0.76rem', color: '#64748b', fontWeight: 500 }}>Haga clic sobre la foto para cambiarla</span>
        </div>

        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label style={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155' }}>Cargo / Función Institucional</label>
          <input 
            value={cargo} 
            onChange={e => setCargo(e.target.value)} 
            placeholder="Ej: Conservador Titular, Funcionario de Mesón..." 
            style={{ borderRadius: 10, padding: '10px 12px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
          />
        </div>

        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <label style={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155' }}>📅 Fecha de Nacimiento</label>
          <input 
            type="date" 
            value={fechaNac} 
            onChange={e => setFechaNac(e.target.value)} 
            style={{ borderRadius: 10, padding: '10px 12px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
          />
          <span style={{ fontSize: '0.73rem', color: '#94a3b8', marginTop: 3, display: 'block' }}>
            Aparecerá en el panel de felicitaciones del mes para sus compañeros.
          </span>
        </div>

        <div style={{ display: 'flex', gap: '0.85rem', justifyContent: 'flex-end', alignItems: 'center', paddingTop: '0.75rem', borderTop: '1px solid #f1f5f9' }}>
          <button 
            type="button"
            className="btn btn-secondary" 
            onClick={onClose} 
            disabled={saving}
            style={{ borderRadius: 10, padding: '10px 18px', fontWeight: 600, fontSize: '0.88rem' }}
          >
            Cancelar
          </button>
          <button 
            type="button"
            className="btn btn-primary" 
            onClick={handleSave} 
            disabled={saving}
            style={{
              borderRadius: 10, padding: '10px 24px', fontWeight: 700, fontSize: '0.92rem',
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: saving ? '#94a3b8' : 'linear-gradient(135deg, #003366 0%, #004d99 100%)',
              boxShadow: saving ? 'none' : '0 4px 14px rgba(0, 51, 102, 0.35)',
              border: 'none', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? (
              <>
                <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                <span>Guardando...</span>
              </>
            ) : (
              <>
                <Save size={16} strokeWidth={2.4} />
                <span>Guardar Perfil</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Dashboard Principal
══════════════════════════════════════════════════════════════ */
export default function DashboardFuncionario() {
  const { user: authUser, login } = useAuth();
  const navigate = useNavigate();

  const [resumen, setResumen] = useState(null);
  const [cumples, setCumples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalPerfil, setModalPerfil] = useState(false);

  const fechaHoy = new Date().toLocaleDateString('es-CL', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const [resRes, cumRes] = await Promise.all([
        api.get('/dashboard/resumen'),
        api.get('/dashboard/cumpleanios'),
      ]);
      setResumen(resRes.data);
      setCumples(cumRes.data);
    } catch {
      toast.error('Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const handlePerfilSaved = (updatedUser) => {
    // Actualizar el contexto de auth con los nuevos datos de perfil
    const stored = JSON.parse(localStorage.getItem('cbr_auth') || '{}');
    const merged = { ...stored, ...updatedUser };
    login(merged);
  };

  if (loading) return (
    <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner />
    </div>
  );

  const mesActual = new Date().toLocaleString('es-CL', { month: 'long' });

  return (
    <div style={{ background: '#f0f4f8', minHeight: '100vh' }}>
      <div className="container" style={{ paddingTop: '2rem', paddingBottom: '4rem' }}>

        {/* ── HERO: Bienvenida + Tarjeta de Perfil ─────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg, #002244 0%, #003366 60%, #0055a4 100%)',
          borderRadius: 24, padding: '2rem 2.5rem', marginBottom: '2rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '1.5rem',
          boxShadow: '0 8px 32px rgba(0,51,102,0.25)',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* Orbes decorativos */}
          <div style={{
            position: 'absolute', top: -60, right: -60,
            width: 240, height: 240, borderRadius: '50%',
            background: 'rgba(255,255,255,0.04)',
          }} />
          <div style={{
            position: 'absolute', bottom: -40, left: '30%',
            width: 180, height: 180, borderRadius: '50%',
            background: 'rgba(255,255,255,0.03)',
          }} />

          {/* Izquierda: Foto + Nombre */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', zIndex: 1 }}>
            <div style={{ position: 'relative' }}>
              <Avatar
                src={authUser?.avatar_url}
                name={authUser?.name}
                size={88}
                style={{ border: '3px solid rgba(255,255,255,0.3)', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
              />
              <button
                onClick={() => setModalPerfil(true)}
                title="Editar perfil"
                style={{
                  position: 'absolute', bottom: 0, right: 0,
                  background: '#d4af37', borderRadius: '50%', border: 'none',
                  padding: 6, cursor: 'pointer', color: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'transform 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <Camera size={13} />
              </button>
            </div>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: 4 }}>
                Hola 👋
              </div>
              <h1 style={{
                color: '#fff', fontSize: '1.8rem', fontWeight: 800,
                margin: 0, lineHeight: 1.1, letterSpacing: '-0.02em'
              }}>
                {authUser?.name || 'Funcionario'}
              </h1>
              <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.88rem', marginTop: 4 }}>
                {authUser?.cargo || authUser?.role || 'Conservador de Bienes Raíces'} ·{' '}
                <button
                  onClick={() => setModalPerfil(true)}
                  style={{
                    background: 'none', border: 'none', color: '#d4af37',
                    cursor: 'pointer', fontSize: '0.83rem', fontWeight: 600, padding: 0
                  }}
                >
                  <Edit3 size={12} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                  Editar perfil
                </button>
              </div>
            </div>
          </div>

          {/* Derecha: Fecha */}
          <div style={{
            zIndex: 1, background: 'rgba(255,255,255,0.1)',
            borderRadius: 16, padding: '1rem 1.5rem', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.15)', textAlign: 'right',
          }}>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Fecha de hoy
            </div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', marginTop: 4, textTransform: 'capitalize' }}>
              {fechaHoy}
            </div>
          </div>
        </div>

        {/* ── STATS ────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
          {/* Trámites */}
          <StatCard icon={AlertCircle} label="Trámites Pendientes"   value={resumen?.tramites_pendientes} color="#dc2626" bg="#fef2f2" linkTo="/tramites" />
          <StatCard icon={CheckCircle2} label="Trámites Completados"    value={resumen?.tramites_completados}   color="#059669" bg="#f0fdf4" linkTo="/tramites" />
          <StatCard icon={FileText}     label="Trám. en curso"   value={resumen?.ultimos_asignados?.length ?? 0} color="#2563eb" bg="#eff6ff" linkTo="/tramites" />

          {/* Solicitudes */}
          <StatCard icon={AlertCircle} label="Solicitudes Pendientes"   value={resumen?.solicitudes_pendientes} color="#dc2626" bg="#fef2f2" linkTo="/gestion-solicitudes?estado=Pendiente&mias=true" />
          <StatCard icon={CheckCircle2} label="Solicitudes Completadas"    value={resumen?.solicitudes_completadas}   color="#059669" bg="#f0fdf4" linkTo="/gestion-solicitudes?estado=Completado&mias=true&activas=false" />
          <StatCard icon={Folder}     label="Mis solicitudes activas"   value={resumen?.solicitudes_curso} color="#2563eb" bg="#eff6ff" linkTo="/gestion-solicitudes?mias=true" />

          {/* Otros */}
          <StatCard icon={Cake}         label={`Cumpleaños en ${mesActual}`} value={cumples.length} color="#d97706" />
        </div>

        {/* ── GRID PRINCIPAL ───────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem' }}>

          {/* Últimos 5 Trámites Terminados */}
          <div style={{
            background: '#fff', borderRadius: 20, padding: '1.5rem',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ background: '#ecfdf5', borderRadius: 10, padding: 8 }}>
                  <CheckCircle2 size={20} color="#059669" />
                </div>
                <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>Últimos Terminados</h3>
              </div>
              <Link to="/tramites" style={{
                fontSize: '0.78rem', color: '#2563eb', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none'
              }}>
                Ver todos <ChevronRight size={14} />
              </Link>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {resumen?.ultimos_terminados?.length > 0 ? resumen.ultimos_terminados.map(t => (
                <div
                  key={t.id}
                  onClick={() => navigate(`/tramite/${t.id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '0.75rem', borderRadius: 12, cursor: 'pointer',
                    background: '#f8fafc', border: '1px solid #f1f5f9',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                  onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
                >
                  <Avatar src={t.assignedTo?.avatar_url} name={t.assignedTo?.name || '?'} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.tipo_inscripcion?.nombre || '—'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                      {t.numero_seguimiento || `#${t.id}`} · {t.nombre_solicitante}
                    </div>
                  </div>
                  <EstadoBadge estado={t.estado?.nombre} />
                </div>
              )) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8', fontSize: '0.88rem' }}>
                  <CheckCircle2 size={32} style={{ opacity: 0.2, marginBottom: 8 }} /><br />
                  No hay trámites terminados aún.
                </div>
              )}
            </div>
          </div>

          {/* Solicitudes en Curso */}
          <div style={{
            background: '#fff', borderRadius: 20, padding: '1.5rem',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ background: '#eff6ff', borderRadius: 10, padding: 8 }}>
                  <Folder size={20} color="#2563eb" />
                </div>
                <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>Mis solicitudes activas</h3>
              </div>
              <Link to="/gestion-solicitudes?mias=true" style={{
                fontSize: '0.78rem', color: '#2563eb', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none'
              }}>
                Ver más <ChevronRight size={14} />
              </Link>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {resumen?.solicitudes_activas?.length > 0 ? resumen.solicitudes_activas.map(t => (
                <div
                  key={`${t.tipo_item}-${t.id}`}
                  onClick={() => navigate(t.link)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '0.75rem', borderRadius: 12, cursor: 'pointer',
                    background: '#f8fafc', border: '1px solid #f1f5f9',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = t.tipo_item === 'Solicitud' ? '#eff6ff' : '#fefce8'}
                  onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
                >
                  <div style={{
                    width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                    background: t.tipo_item === 'Solicitud' ? 'linear-gradient(135deg, #e0f2fe, #bae6fd)' : 'linear-gradient(135deg, #fffbeb, #fef3c7)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: t.tipo_item === 'Solicitud' ? '#0284c7' : '#d97706', 
                    fontWeight: 800, fontSize: '0.8rem',
                    border: t.tipo_item === 'Solicitud' ? '1px solid #7dd3fc' : '1px solid #fde68a',
                  }}>
                    {t.tipo_item === 'Solicitud' ? <FileText size={18} /> : t.id}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.display_title}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.display_subtitle}
                    </div>
                  </div>
                  <EstadoBadge estado={t.estado?.nombre} />
                </div>
              )) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8', fontSize: '0.88rem' }}>
                  <Folder size={32} style={{ opacity: 0.2, marginBottom: 8 }} /><br />
                  No tienes solicitudes activas.
                </div>
              )}
            </div>
          </div>

          {/* 🎂 Cumpleaños del Mes */}
          <div style={{
            background: 'linear-gradient(135deg, #1a6b6b, #0d5555)',
            borderRadius: 20, padding: '1.5rem',
            boxShadow: '0 2px 12px rgba(0,0,0,0.12)', gridColumn: 'span 1',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: 8 }}>
                  <Cake size={20} color="#fff" />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1rem', color: '#fff' }}>Cumpleaños del Mes</h3>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.76rem', textTransform: 'capitalize' }}>
                    {mesActual}
                  </div>
                </div>
              </div>
            </div>

            {cumples.length > 0 ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                gap: '0.75rem',
              }}>
                {cumples.map(p => (
                  <CumpleanioCard key={p.id} persona={p} />
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center', padding: '2.5rem 1rem',
                color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem',
              }}>
                <Cake size={42} style={{ opacity: 0.2, marginBottom: 12 }} /><br />
                No hay cumpleaños este mes.<br />
                <button
                  onClick={() => setModalPerfil(true)}
                  style={{
                    marginTop: 12, background: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.25)', color: '#fff',
                    borderRadius: 20, padding: '6px 16px', cursor: 'pointer',
                    fontSize: '0.78rem', fontWeight: 600,
                  }}
                >
                  📅 Agregar mi fecha
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── TABLA: Últimos Trámites Asignados ─────────────────────── */}
        <div style={{
          background: '#fff', borderRadius: 20, padding: '1.5rem',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0',
          marginTop: '1.5rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ background: '#f5f3ff', borderRadius: 10, padding: 8 }}>
                <TrendingUp size={20} color="#7c3aed" />
              </div>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>Últimos trámites asignados a mí</h3>
            </div>
            <Link to="/tramites" style={{
              fontSize: '0.78rem', color: '#2563eb', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none'
            }}>
              Ver todos <ExternalLink size={13} />
            </Link>
          </div>

          {resumen?.ultimos_asignados?.length > 0 ? (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Solicitante</th>
                    <th>Tipo de trámite</th>
                    <th>Estado</th>
                    <th>Fecha ingreso</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {resumen.ultimos_asignados.map(t => (
                    <tr key={t.id} onClick={() => navigate(`/tramite/historial/${t.id}`)} style={{ cursor: 'pointer' }}>
                      <td style={{ fontWeight: 700, color: '#003366' }}>
                        {t.numero_seguimiento || `#${t.id}`}
                      </td>
                      <td style={{ fontWeight: 600 }}>{t.nombre_solicitante}</td>
                      <td style={{ color: '#64748b', fontSize: '0.87rem' }}>{t.tipo_inscripcion?.nombre || '—'}</td>
                      <td><EstadoBadge estado={t.estado?.nombre} /></td>
                      <td style={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                        {t.created_at ? new Date(t.created_at).toLocaleDateString('es-CL') : '—'}
                      </td>
                      <td>
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ padding: '3px 10px', fontSize: '0.78rem' }}
                          onClick={e => { e.stopPropagation(); navigate(`/tramite/historial/${t.id}`); }}
                        >
                          Ver →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
              <Users size={42} style={{ opacity: 0.15, marginBottom: 12 }} /><br />
              No tienes trámites asignados aún.
            </div>
          )}
        </div>
      </div>

      {/* ── Modal Perfil ─────────────────────────────────────────── */}
      {modalPerfil && (
        <ModalPerfil
          user={authUser}
          onClose={() => setModalPerfil(false)}
          onSaved={handlePerfilSaved}
        />
      )}
    </div>
  );
}
